import { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Row, Spinner } from "react-bootstrap";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Flight from "../../../models/flight";
import { getFlightStopPoints } from "../../../models/flightLegs";
import { EntityDetailsDisplayInfoFieldWb } from "../EntityDetailsDisplayInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCaretRight, faCalculator } from '@fortawesome/free-solid-svg-icons'

interface EntityFlightWbComponentProps<Entity extends FieldValues> {
    displayInfoInput: EntityDetailsDisplayInfoFieldWb<Entity>;
    flight: Flight;
    handleSaveFlight: (flight: Flight) => Promise<Flight>;
    clearWbCalculation: () => void;
    skipWbCalculation: () => void;
}

function EntityFlightWbComponent<Entity extends FieldValues>({ displayInfoInput, flight, handleSaveFlight, clearWbCalculation, skipWbCalculation}: EntityFlightWbComponentProps<Entity>) {
    const navigate = useNavigate();

    const [openingWb, setOpeningWb] = useState(false);
    //Ref-based re-entry guard: state updates are async, so a fast double-tap (or a tap
    //while a slow network request is in flight) could fire handleSaveFlight twice and
    //CREATE THE FLIGHT TWICE on an add page. The ref blocks synchronously.
    const openingWbRef = useRef(false);

    //Opening W&B saves the flight first, and that save can be refused (an incomplete flight, a server
    //rule). Unhandled, the rejection escaped as an uncaught promise and the user saw "[object Object]"
    //instead of the reason — show it here instead. Redux Toolkit's unwrap() re-throws a serialized
    //object, so read `message` off it rather than assuming an Error or a string.
    const [saveError, setSaveError] = useState<string | undefined>();

    const saveFlightAndOpenWb = async () => {
      if (openingWbRef.current) return;
      openingWbRef.current = true;
      setOpeningWb(true);
      setSaveError(undefined);
      //Whether this is a brand-new flight has to be read BEFORE saving — afterwards it always has an id.
      //Testing the URL instead (pathname.endsWith("add")) only ever matched the legacy /flights/add, so
      //once the rules-aware /flights/add/vfr|ifr routes arrived the relative navigate produced
      ///flights/add/vfr/wb — a 404, with the flight saved and the user stranded.
      const isNewFlight = flight.id === undefined;
      try {
        if (isNewFlight || flight.briefing!.status === "not-started" || flight.briefing!.status === "error") {
          const savedFlight = await handleSaveFlight(flight);
          if (isNewFlight){
            navigate(`/flights/${savedFlight.id}/wb`)
            return
          }
        }
        navigate(`wb`)
      } catch (e: any) {
        setSaveError(typeof e === "string" ? e : e?.message ?? "Could not save the flight. Check the required fields above.");
      } finally {
        openingWbRef.current = false;
        setOpeningWb(false);
      }
    }

    const { aircraft, passengers, fuelOnBoard, pic, sic, deadheadCrew, studentPilot, route, passengerLegs, totalEETMins } = flight;
    //the stops (saved or custom route) define the legs; any change to them re-seats every leg
    const stopsKey = getFlightStopPoints(flight).map((p) => p.name).join(">");
    
    const [okState, setOkState] = useState<{
      aircraft: Flight["aircraft"];
      passengers: Flight["passengers"];
      fuelOnBoard: Flight["fuelOnBoard"];
      pic: Flight["pic"];
      sic: Flight["sic"];
      deadheadCrew: Flight["deadheadCrew"];
      studentPilot: Flight["studentPilot"];
      route: Flight["route"];
      passengerLegs: Flight["passengerLegs"];
      stopsKey: string;
      totalEETMins: number;
    } | undefined>();

    useEffect(() => {
      if ((flight.wb?.status === "ok" || flight.wb?.status === "error") && okState === undefined) {
        setOkState({
          aircraft,
          passengers,
          fuelOnBoard,
          pic,
          sic,
          deadheadCrew,
          studentPilot,
          route,
          passengerLegs,
          stopsKey,
          totalEETMins,
        });
      } else if (
        okState !== undefined &&
        (flight.wb?.status === "ok" || flight.wb?.status === "error") &&
        (okState.aircraft?.id !== aircraft?.id ||
          !(
            okState.passengers.length === passengers.length &&
            okState.passengers.every((p, i) => p.id === passengers[i].id && p.weight === passengers[i].weight)
          ) ||
          !(
            okState.fuelOnBoard.length === fuelOnBoard.length &&
            okState.fuelOnBoard.every((f, i) =>
                f.mass === fuelOnBoard[i].mass &&
                f.station.id === fuelOnBoard[i].station.id
            )
          ) ||
          okState.pic?.id !== pic?.id ||
          okState.sic?.id !== sic?.id ||
          okState.deadheadCrew?.id !== deadheadCrew?.id ||
          okState.studentPilot?.id !== studentPilot?.id ||
          okState.route?.id !== route?.id ||
          //trip burn (landing weight) follows the EET; per-leg seating follows the stops and who flies which leg
          okState.totalEETMins !== totalEETMins ||
          okState.stopsKey !== stopsKey ||
          JSON.stringify(okState.passengerLegs ?? {}) !== JSON.stringify(passengerLegs ?? {}))
      ) {
        clearWbCalculation();
        setOkState(undefined);
      }
    }, [aircraft, flight, fuelOnBoard, passengers, pic,  sic, deadheadCrew, studentPilot, route, passengerLegs, stopsKey, totalEETMins, okState, clearWbCalculation])

    const calculateWbBtnElement = (
      <Button variant="light" disabled={openingWb} onClick={() => saveFlightAndOpenWb()}>
        {openingWb ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" /> Saving...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faCalculator} color="black" className="pe-2" /> Calculate W&B{" "}
            <FontAwesomeIcon icon={faCaretRight} color="black" />
          </>
        )}
      </Button>
    )

    return (
      <>
        {(flight.wbRevisions?.length ?? 0) > 0 && (
          <Alert variant="warning" className="py-2">
            <small>
              This flight has {flight.wbRevisions!.length} last-minute W&amp;B revision{flight.wbRevisions!.length > 1 ? "s" : ""}. Changing passengers, fuel,
              crew, aircraft or route, or recalculating the W&amp;B, discards {flight.wbRevisions!.length > 1 ? "them" : "it"} and the no-shows when you save.
            </small>
          </Alert>
        )}
        {saveError && (
          <Alert variant="danger" className="py-2">
            <small>{saveError}</small>
          </Alert>
        )}
        {aircraft.wb && aircraft.wb.profileName && aircraft.wb.emptyMass ? (
          <>
            <Row>
              <div className="fw-bold">
                {(flight.wb === undefined || flight.wb.status === "not-calculated") && (
                  <Alert variant={"danger"}>
                    <div className="d-flex align-items-center justify-content-between">
                      <span>Weight and Balance not calculated yet.</span>
                      {calculateWbBtnElement}
                    </div>
                  </Alert>
                )}
                {flight.wb?.status === "error" && (
                  <Alert variant={"danger"}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex flex-column justify-content-between">
                        {flight.wb.errors!.map((error, index) => (
                          <span key={index}>• {error}</span>
                        ))}
                      </div>
                      {calculateWbBtnElement}
                    </div>
                  </Alert>
                )}
                {flight.wb?.status === "ok" && (
                  <Alert variant={"success"}>
                    <div className="d-flex align-items-center justify-content-between">
                      Weight and Balance OK.
                      {calculateWbBtnElement}
                    </div>
                  </Alert>
                )}
              </div>
            </Row>
          </>
        ) : (
          <Alert variant={flight.wb?.status === "no-airctaft-data" ? "warning" : "danger"}>
            <div className="d-flex flex-column justify-content-between">
              <strong>Missing Aircraft's Weight and Balance info.</strong>
              {flight.wb?.status !== "no-airctaft-data" && (
                <Row className="d-flex align-items-center justify-content-between">
                  <Col>
                    <small>Please fill in the Profile Name and Empty Mass on Aircraft's page, or skip it for this flight.</small>
                  </Col>
                  <Col xs={"auto"}>
                    <Button variant="light" onClick={() => skipWbCalculation()}>
                      Skip W&B
                    </Button>
                  </Col>
                </Row>
              )}
            </div>
          </Alert>
        )}
      </>
    );
}

export default EntityFlightWbComponent